import { Button, Switch, Text, VSpacer } from '@/components/DesignSystem';
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { ApiUser } from '@api/interfaces';
import { Stack } from '@mui/system';
import { AccountGroupRole } from '@shared/enums';
import { formatPhoneNumber } from '@shared/utilities';
import { getAccountName, getFormattedAddress } from '@shared/utilities/UserUtilities';
import React from 'react';
import { useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { CustomerRoleBadge } from "@/pages/CustomerDashboard/CustomerRoleBadge";
import { UserApi } from '@/utilities/api/UserApi';
import { useTranslation } from 'react-i18next';

interface LinkedAccountCardProps {
  account: ApiUser,
  onViewAccount?: (customer: ApiUser) => void,
}

export const LinkedAccountCard = ({
  account,
  onViewAccount,
}: LinkedAccountCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  if (!account) {
    return null;
  }

  const formattedAddress = getFormattedAddress(account);
  const formattedPhoneNumber = account.telephone
    && formatPhoneNumber(account.telephone, 'paren');

  return (
    <Stack
      alignItems="center"
      direction="row"
      justifyContent="space-between"
      key={account.id}
      py="16px"
    >
      <Stack>
        <Text category="title-medium">{getAccountName(account)}</Text>
        {!!(formattedAddress || formattedPhoneNumber) && (
          <Text 
            category="body-medium"
            sx={{ 
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%',
            }}
          >
            {[formattedAddress, formattedPhoneNumber].filter(Boolean).join(' • ')}
          </Text>
        )}
        <VSpacer size="3" />
        <Stack alignItems="center" direction="row">
          {!!account.accountGroupRole && (
            <CustomerRoleBadge
              accountGroupRole={account.accountGroupRole}
            />
          )}
        </Stack>
        {account.accountGroupRole !== AccountGroupRole.Secondary && (
          <>
            <VSpacer size="4" />
            <Switch
              checked={account.sendNotifications}
              label={
                <Text category="title-small" ml="12px">
                  {t($ => $.customers.linkedAccounts.notificationsOn)}
                </Text>
              }
              onClick={async () => {
                await UserApi.update(
                  account.id,
                  { sendNotifications: !account.sendNotifications },
                );
                void queryClient.invalidateQueries(QueryKeys.GET_LINKED_ACCOUNTS);
              }}
              testID={`${account.id}-notifications-switch`}
            />
          </>
        )}
      </Stack>
      <Button
        color="inherit"
        onClick={() => (
          onViewAccount && account
            ? onViewAccount(account)
            : navigate(Routes.CUSTOMER_DETAILS.replace(':id', account.id))
        )}
        sx={{ borderColor: 'rgba(0, 0, 0, 0.16)', marginLeft: '12px' }}
        testID="subwallet-card-details-button"
        variant="outlined"
      >
        {t($ => $.common.actions.viewAccount)}
      </Button>
    </Stack>
  );
};
